import { Copy } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { cn } from '@/lib/utils';

interface ConnectDialogProps {
  /** `host:port` of the server; the trigger is disabled when null. */
  address: string | null;
  /** Invite code of the running game; the row is hidden when null. */
  inviteCode?: string | null;
  serverName: string;
  triggerClassName?: string;
}

/**
 * "Connect" popup (legacy `#server-connect` modal): the in-game console
 * command and, when the running game exposes one, its invite code. Both rows
 * copy to the clipboard on click. Radix owns open state and returns focus to
 * the trigger on close.
 */
export function ConnectDialog({
  address,
  inviteCode = null,
  serverName,
  triggerClassName,
}: ConnectDialogProps) {
  const { t } = useTranslation('common');
  const command = address ? `connect ${address}` : null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="primary" size="sm" className={triggerClassName} disabled={!command}>
          {t('connect')}
        </Button>
      </DialogTrigger>
      {command ? (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('connectDialog.title', { server: serverName })}</DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="connectDialog.description"
                ns="common"
                components={{ key: <strong className="text-strong" /> }}
              />
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <CopyRow label={t('copy.command')} value={command} />
            {inviteCode ? (
              <>
                <p className="text-dim font-mono text-xs">{t('connectDialog.orInvite')}</p>
                <CopyRow label={t('copy.inviteCode')} value={inviteCode} />
              </>
            ) : null}
            <p className="text-faint text-[10px] uppercase tracking-wide">
              {t('copy.clickToCopy')}
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="default" size="sm">
                {t('actions.close')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation('common');
  const { copied, copy } = useCopyToClipboard();

  const handleCopy = async () => {
    if (await copy(value)) {
      toast.success(t('copy.copiedValue', { value }));
    } else {
      toast.error(t('copy.failed'));
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('copy.copyLabel', { label: label.toLowerCase() })}
      className={cn(
        'bevel-in bg-code-bg flex w-full items-center gap-3 px-3 py-2 text-left outline-none',
        'transition-[filter] hover:brightness-118 focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-faint text-[10px] uppercase tracking-wide">{label}</span>
        <code className="text-code-tx truncate font-mono text-sm">{value}</code>
      </span>
      <span className="text-dim ml-auto flex shrink-0 items-center gap-1 font-mono text-[11px]">
        {copied ? t('copy.copied') : <Copy className="size-4" aria-hidden />}
      </span>
    </button>
  );
}
